import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { ServicesURL } from "@utils/utilsApi";
import { fetchAllPages } from "@utils/fetchAllPages";
import { EditorialPerInitiative } from "../../AddActivity/types";
import { mapCourseDetailToWizardData } from "../payload/mapCourseDetailToWizardData";
import { CourseWizardData } from "../types";

interface UseCourseDetailV2Args {
  corporateId: string;
  idCourse: string;
}

interface UseCourseDetailV2Return {
  wizardData: CourseWizardData | null;
  initialInitiativeIds: number[];
  initialEditorial: Record<number, EditorialPerInitiative>;
  isLoading: boolean;
  error: string | null;
  mutate: () => Promise<unknown>;
}

const swrFetcher = (url: string) => fetcher(url, MethodType.GET);
const childrenFetcher = (url: string) =>
  fetchAllPages<Record<string, any>>(url, 100, false);

export const useCourseDetailV2 = ({
  corporateId,
  idCourse,
}: UseCourseDetailV2Args): UseCourseDetailV2Return => {
  const detailUrl =
    corporateId && idCourse
      ? `${ServicesURL["learning-catalogue"]}/${corporateId}/learning-objects/courses/${idCourse}`
      : null;

  const {
    data: detail,
    error: detailError,
    isLoading: isDetailLoading,
    mutate: mutateDetail,
  } = useSWR<Record<string, any>>(detailUrl, swrFetcher);

  const childrenUrl =
    corporateId && idCourse
      ? ServicesURL.getCourseChildren
          .replace(":idCorporate", corporateId)
          .replace(":idCourse", idCourse)
      : null;

  const {
    data: childrenResponse,
    error: childrenError,
    isLoading: isChildrenLoading,
    mutate: mutateChildren,
  } = useSWR<Record<string, any>[]>(childrenUrl, childrenFetcher);

  const children = useMemo(() => childrenResponse ?? [], [childrenResponse]);

  const initiativeIds: number[] = useMemo(() => {
    const list = detail?.initiative;
    if (!Array.isArray(list)) return [];
    return list
      .map((it: any) => Number(it?.id))
      .filter((n) => Number.isFinite(n));
  }, [detail]);

  const [editorial, setEditorial] = useState<
    Record<number, EditorialPerInitiative>
  >({});
  const [isEditorialLoading, setIsEditorialLoading] = useState(false);
  const [editorialError, setEditorialError] = useState<string | null>(null);

  useEffect(() => {
    if (!detail || initiativeIds.length === 0) {
      setEditorial({});
      return;
    }
    let cancelled = false;
    setIsEditorialLoading(true);
    setEditorialError(null);

    const loId = Number(detail.id);
    const buildUrl = (template: string, initiativeId: number): string =>
      template
        .replace(":idCorporate", corporateId)
        .replace(":idLO", String(loId))
        .replace(":initiativeId", String(initiativeId));

    const fetchOne = async (
      initiativeId: number,
    ): Promise<[number, EditorialPerInitiative]> => {
      const topicsUrl = buildUrl(ServicesURL.catalogLoTopics, initiativeId);
      const categoriesUrl = buildUrl(
        ServicesURL.catalogLoCategories,
        initiativeId,
      );
      const [topicsRes, categoriesRes] = await Promise.all([
        fetchAllPages(topicsUrl),
        fetchAllPages(categoriesUrl),
      ]);

      return [
        initiativeId,
        {
          selected_topic_ids: topicsRes
            .map((it: any) => Number(it?.id))
            .filter((n) => Number.isFinite(n)),
          selected_carousel_ids: categoriesRes
            .map((it: any) => Number(it?.id))
            .filter((n) => Number.isFinite(n)),
          //TODO skills
          selected_skill_ids: [],
        },
      ];
    };

    Promise.all(initiativeIds.map(fetchOne))
      .then((pairs) => {
        if (cancelled) return;
        const map: Record<number, EditorialPerInitiative> = {};
        for (const [id, eds] of pairs) map[id] = eds;
        setEditorial(map);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setEditorialError(e?.message || "Errore fetch editoriali");
      })
      .finally(() => {
        if (!cancelled) setIsEditorialLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [detail, initiativeIds, corporateId]);

  const wizardData = useMemo<CourseWizardData | null>(() => {
    if (!detail) return null;
    return mapCourseDetailToWizardData(detail, editorial, children);
  }, [detail, editorial, children]);

  return {
    wizardData,
    initialInitiativeIds: initiativeIds,
    initialEditorial: editorial,
    isLoading: isDetailLoading || isChildrenLoading || isEditorialLoading,
    error:
      detailError?.message || childrenError?.message || editorialError || null,
    mutate: () => Promise.all([mutateDetail(), mutateChildren()]),
  };
};
